// The Supabase client, created once.
//
// The publishable key is safe in client code -- it grants exactly what Row Level Security allows and nothing
// more. The service_role key and the management token are NEVER in this repo and never reach the browser.
//
// ⚠ MISSING CONFIG IS SURFACED, NOT SWALLOWED. Netlify scopes environment variables, so a Deploy Preview or a
// branch deploy can miss a production-only value. The Character Generator embed in Tabletop Hub learned this
// the expensive way: an unset URL fell through to a default nobody was running, and it rendered as a blank grey
// panel with no hint of why -- which reads as "the tool is broken" rather than "this deploy isn't configured".
// So we detect it and say so, in those words, on screen.
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { sameMomentFetch, signedLinksBelongTo, withSignedUrlMemory } from "./signed-url-cache";

const URL_ENV = "VITE_SUPABASE_URL";
const KEY_ENV = "VITE_SUPABASE_PUBLISHABLE_KEY";

const url = import.meta.env[URL_ENV] as string | undefined;
const key = import.meta.env[KEY_ENV] as string | undefined;

/** Which required env vars this build is missing. Empty array = configured. Read at render, shown to the user. */
export function missingConfig(): string[] {
  const missing: string[] = [];
  if (!url) missing.push(URL_ENV);
  if (!key) missing.push(KEY_ENV);
  return missing;
}

export const isConfigured = missingConfig().length === 0;

// Created even when unconfigured, with obviously-invalid placeholders, so importing this module never throws at
// load time. The UI checks `isConfigured` and renders the explanation instead of a broken app; nothing that
// touches the network runs before that check.
export const supabase: SupabaseClient = createClient(
  url ?? "https://unconfigured.invalid",
  key ?? "unconfigured",
  {
    auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
    // The same read asked twice in the same instant is sent once (lib/signed-url-cache.ts `sameMomentFetch`).
    global: { fetch: sameMomentFetch((...args) => fetch(...args)) },
  },
);

// ⚠ ONE LINK PER FILE FOR AS LONG AS IT IS GOOD (lib/signed-url-cache.ts): a fresh signed link on every load was a new address
// every time, so the browser re-downloaded every map, face and thumbnail on every page load. Wrapped here, once, so every
// caller of `supabase.storage.from(…)` gets it without knowing.
{
  const storage = supabase.storage as unknown as { from: (bucket: string) => object };
  const rawFrom = storage.from.bind(supabase.storage);
  storage.from = (bucket: string) => withSignedUrlMemory(bucket, rawFrom(bucket));
  try {
    void supabase.auth.getSession().then(({ data }) => signedLinksBelongTo(data.session?.user.id ?? null));
    supabase.auth.onAuthStateChange((event, s) => { if (event === "SIGNED_IN" || event === "SIGNED_OUT") signedLinksBelongTo(s?.user.id ?? null); });
  } catch { /* unconfigured: nothing is signed either */ }
}
